import { AspectRatio, FieldConfig } from './engineTypes';

// ── Aspect Ratios ──
export const ALL_ASPECT_RATIOS: { value: AspectRatio; label: string; category: string }[] = [
  { value: '1:1', label: '1:1 Square', category: 'Standard' },
  { value: '4:5', label: '4:5 Instagram Portrait', category: 'Portrait' },
  { value: '3:4', label: '3:4 Classic Portrait', category: 'Portrait' },
  { value: '2:3', label: '2:3 Full Portrait', category: 'Portrait' },
  { value: '5:7', label: '5:7 Medium Portrait', category: 'Portrait' },
  { value: '9:16', label: '9:16 Vertical / Story', category: 'Portrait' },
  { value: '1:2', label: '1:2 Tall Vertical', category: 'Portrait' },
  { value: '5:4', label: '5:4 Landscape', category: 'Landscape' },
  { value: '4:3', label: '4:3 Classic TV', category: 'Landscape' },
  { value: '3:2', label: '3:2 Classic Photo', category: 'Landscape' },
  { value: '7:5', label: '7:5 Wide Photo', category: 'Landscape' },
  { value: '16:9', label: '16:9 Widescreen', category: 'Landscape' },
  { value: '1.85:1', label: '1.85:1 Film Standard', category: 'Cinematic' },
  { value: '2:1', label: '2:1 Univisium', category: 'Cinematic' },
  { value: '21:9', label: '21:9 Ultra-Wide', category: 'Cinematic' },
  { value: '2.39:1', label: '2.39:1 Anamorphic', category: 'Cinematic' },
];

// ── Style Presets (sourced from dynamic registry) ──
import { STYLE_OPTIONS } from '@/data/options/styleOptions';

export const STYLE_PRESETS = [
  { value: 'none', label: 'No Style Preset' },
  ...STYLE_OPTIONS.map(s => ({ value: s.id, label: s.label })),
];

// ══════════════════════════════════════
// CAMERA PANEL FIELDS
// ══════════════════════════════════════

// NOTE: many selector lists include a hidden "none" choice that is
// presented as an empty placeholder to the user. This allows the canvas to
// start with no active camera/lighting/etc when the workspace is fresh or after
// the user clicks "Clean". The actual value is still stored as 'none' so the
// pipeline can infer from the prompt text later.
export const CAMERA_FIELDS: FieldConfig[] = [
  {
    type: 'select', key: 'lens', label: 'Focal Length / Lens',
    tooltip: 'Lens focal length determines field of view and compression',
    options: [
      { value: 'none', label: 'None' },
      { value: '8mm', label: '8mm Fisheye' },
      { value: '14mm', label: '14mm Ultra Wide' },
      { value: '16mm', label: '16mm Super Wide' },
      { value: '20mm', label: '20mm Wide' },
      { value: '24mm', label: '24mm Wide Angle' },
      { value: '28mm', label: '28mm Moderate Wide' },
      { value: '35mm', label: '35mm Standard Wide' },
      { value: '40mm', label: '40mm Pancake' },
      { value: '50mm', label: '50mm Normal / Nifty Fifty' },
      { value: '58mm', label: '58mm Classic Portrait' },
      { value: '85mm', label: '85mm Portrait' },
      { value: '100mm_macro', label: '100mm Macro' },
      { value: '105mm', label: '105mm Portrait Tele' },
      { value: '135mm', label: '135mm Tele Portrait' },
      { value: '200mm', label: '200mm Telephoto' },
      { value: '300mm', label: '300mm Long Tele' },
      { value: '400mm', label: '400mm Super Tele' },
      { value: '600mm', label: '600mm Extreme Tele' },
      { value: 'anamorphic', label: 'Anamorphic Cine' },
      { value: 'tilt_shift', label: 'Tilt-Shift' },
      { value: 'pinhole', label: 'Pinhole' },
      { value: 'lensbaby', label: 'Lensbaby Selective' },
    ],
  },
  {
    type: 'select', key: 'cameraType', label: 'Camera Type',
    tooltip: 'Type of camera body affects rendering characteristics',
    advancedOnly: true,
    options: [
      { value: 'none', label: 'None' },
      { value: 'dslr', label: 'DSLR' },
      { value: 'cinema', label: 'Cinema Camera (RED/ARRI)' },
      { value: 'imax', label: 'IMAX 65mm' },
      { value: 'drone', label: 'Drone Camera' },
      { value: 'handheld', label: 'Handheld / Phone' },
      { value: 'steadicam', label: 'Steadicam Rig' },
      { value: 'fpv', label: 'FPV Drone' },
      { value: 'gopro', label: 'GoPro / Action Cam' },
      { value: 'virtual', label: 'Virtual / CG Camera' },
      { value: 'medium_format', label: 'Medium Format' },
      { value: 'large_format', label: 'Large Format 4x5' },
      { value: 'instant', label: 'Instant / Polaroid' },
    ],
  },
  {
    type: 'select', key: 'shotType', label: 'Shot Type / Framing',
    tooltip: 'How much of the subject is visible in frame',
    options: [
      { value: 'none', label: 'None' },
      { value: 'extreme_close', label: 'Extreme Close-Up (ECU)' },
      { value: 'close_up', label: 'Close-Up (CU)' },
      { value: 'medium_close', label: 'Medium Close-Up (MCU)' },
      { value: 'medium', label: 'Medium Shot (MS)' },
      { value: 'cowboy', label: 'Cowboy / American Shot' },
      { value: 'medium_wide', label: 'Medium Wide (MWS)' },
      { value: 'wide', label: 'Wide Shot (WS)' },
      { value: 'extreme_wide', label: 'Extreme Wide / Establishing' },
      { value: 'full_body', label: 'Full Body Shot' },
      { value: 'two_shot', label: 'Two Shot' },
      { value: 'group', label: 'Group Shot' },
      { value: 'insert', label: 'Insert / Detail Shot' },
      { value: 'over_shoulder', label: 'Over-the-Shoulder' },
      { value: 'dutch_angle', label: 'Dutch Angle' },
      { value: 'panoramic', label: 'Panoramic' },
    ],
  },
  {
    type: 'select', key: 'perspective', label: 'Camera Angle / Perspective',
    tooltip: 'Vertical angle of the camera relative to subject',
    options: [
      { value: 'none', label: 'None' },
      { value: 'eye_level', label: 'Eye Level' },
      { value: 'high_angle', label: 'High Angle (Looking Down)' },
      { value: 'low_angle', label: 'Low Angle (Looking Up)' },
      { value: 'birds_eye', label: "Bird's Eye / Top-Down" },
      { value: 'worms_eye', label: "Worm's Eye (Ground Up)" },
      { value: 'dutch_angle', label: 'Dutch Angle (Tilted)' },
      { value: 'three_quarter', label: 'Three-Quarter View' },
      { value: 'profile', label: 'Profile / Side View' },
      { value: 'rear', label: 'Rear / From Behind' },
      { value: 'aerial', label: 'Aerial / Drone' },
      { value: 'pov', label: 'POV / First Person' },
      { value: 'crane', label: 'Crane Shot' },
    ],
  },
  {
    type: 'select', key: 'cameraMovement', label: 'Camera Movement',
    tooltip: 'How the camera moves during the shot',
    options: [
      { value: 'static', label: 'Static / Locked' },
      { value: 'dolly_in', label: 'Dolly In (Push)' },
      { value: 'dolly_out', label: 'Dolly Out (Pull)' },
      { value: 'crane_up', label: 'Crane Up' },
      { value: 'crane_down', label: 'Crane Down' },
      { value: 'orbit', label: 'Orbit Around' },
      { value: 'tracking', label: 'Tracking / Trucking' },
      { value: 'parallax', label: 'Parallax Shift' },
      { value: 'whip_pan', label: 'Whip Pan' },
      { value: 'slow_push', label: 'Slow Push In' },
      { value: 'pan_left', label: 'Pan Left' },
      { value: 'pan_right', label: 'Pan Right' },
      { value: 'tilt_up', label: 'Tilt Up' },
      { value: 'tilt_down', label: 'Tilt Down' },
      { value: 'zoom_in', label: 'Zoom In' },
      { value: 'zoom_out', label: 'Zoom Out' },
      { value: 'handheld_shake', label: 'Handheld Shake' },
      { value: 'roll', label: 'Barrel Roll' },
    ],
  },
  {
    type: 'select', key: 'sensorSize', label: 'Sensor Size',
    tooltip: 'Sensor size affects depth of field and perspective',
    advancedOnly: true,
    options: [
      { value: 'full_frame', label: 'Full Frame (36x24mm)' },
      { value: 'aps_c', label: 'APS-C (23.5x15.6mm)' },
      { value: 'super35', label: 'Super 35mm (Cine)' },
      { value: 'medium_format', label: 'Medium Format (44x33mm)' },
      { value: 'micro43', label: 'Micro Four Thirds' },
      { value: 'large_format', label: 'Large Format 4x5"' },
      { value: 'imax', label: 'IMAX (70mm)' },
      { value: '1inch', label: '1-inch Sensor' },
    ],
  },
  {
    type: 'select', key: 'dof', label: 'Depth of Field',
    tooltip: 'Controls how much of the scene is in sharp focus',
    options: [
      { value: 'hyper_deep', label: 'Hyperfocal — Max Sharpness' },
      { value: 'deep', label: 'Deep — Everything Sharp' },
      { value: 'moderate', label: 'Moderate — Balanced' },
      { value: 'shallow', label: 'Shallow — Soft Background' },
      { value: 'very_shallow', label: 'Very Shallow — Dreamy Bokeh' },
      { value: 'razor_thin', label: 'Razor Thin — Sliver Focus' },
      { value: 'tilt_shift_dof', label: 'Tilt-Shift Miniature' },
    ],
  },
  {
    type: 'slider', key: 'dofIntensity', label: 'DOF Intensity',
    tooltip: 'Fine-tune the depth of field blur amount',
    advancedOnly: true, min: 0, max: 100, step: 5,
  },
  {
    type: 'select', key: 'focusMode', label: 'Focus Mode',
    tooltip: 'Where the camera locks focus',
    options: [
      { value: 'center', label: 'Center Focus' },
      { value: 'subject', label: 'Subject Tracking' },
      { value: 'foreground', label: 'Foreground Focus' },
      { value: 'background', label: 'Background Focus' },
      { value: 'split_focus', label: 'Split Diopter' },
      { value: 'zone', label: 'Zone Focus' },
      { value: 'soft_focus', label: 'Soft Focus / Dreamy' },
      { value: 'rack_focus', label: 'Rack Focus' },
    ],
  },
  {
    type: 'select', key: 'shutterSpeed', label: 'Shutter Speed',
    tooltip: 'Affects motion blur. Slower = more blur',
    advancedOnly: true,
    options: [
      { value: '1/8000', label: '1/8000s — Frozen Action' },
      { value: '1/4000', label: '1/4000s — Sports' },
      { value: '1/2000', label: '1/2000s — Fast Action' },
      { value: '1/1000', label: '1/1000s — Sharp Motion' },
      { value: '1/500', label: '1/500s — Standard' },
      { value: '1/250', label: '1/250s — Slight Blur' },
      { value: '1/125', label: '1/125s — Normal' },
      { value: '1/60', label: '1/60s — Cinematic 24fps' },
      { value: '1/30', label: '1/30s — Motion Blur' },
      { value: '1/15', label: '1/15s — Heavy Blur' },
      { value: '1/4', label: '1/4s — Light Trails' },
      { value: '1s', label: '1s — Long Exposure' },
      { value: '30s', label: '30s — Star Trails' },
      { value: 'bulb', label: 'Bulb — Ultra Long' },
    ],
  },
  {
    type: 'select', key: 'isoControl', label: 'ISO',
    tooltip: 'Sensor sensitivity. Higher = more grain/noise',
    advancedOnly: true,
    options: [
      { value: '50', label: 'ISO 50 — Ultra Clean' },
      { value: '100', label: 'ISO 100 — Clean' },
      { value: '200', label: 'ISO 200 — Low Noise' },
      { value: '400', label: 'ISO 400 — Standard' },
      { value: '800', label: 'ISO 800 — Slight Grain' },
      { value: '1600', label: 'ISO 1600 — Visible Grain' },
      { value: '3200', label: 'ISO 3200 — Noisy' },
      { value: '6400', label: 'ISO 6400 — Heavy Grain' },
      { value: '12800', label: 'ISO 12800 — Extreme Noise' },
      { value: '25600', label: 'ISO 25600 — Max Noise' },
    ],
  },
  {
    type: 'slider', key: 'motionBlur', label: 'Motion Blur',
    tooltip: 'Amount of motion blur effect', advancedOnly: true,
    min: 0, max: 100, step: 5,
  },
];

// ══════════════════════════════════════
// LIGHTING PANEL FIELDS
// ══════════════════════════════════════

export const LIGHTING_FIELDS: FieldConfig[] = [
  {
    type: 'select', key: 'lightStyle', label: 'Light Type / Style',
    tooltip: 'The primary lighting technique used',
    options: [
      { value: 'none', label: 'None' },
      { value: 'natural', label: 'Natural Ambient' },
      { value: 'key', label: 'Key Light' },
      { value: 'softbox', label: 'Softbox' },
      { value: 'rim', label: 'Rim / Edge Light' },
      { value: 'backlight', label: 'Backlight' },
      { value: 'volumetric', label: 'Volumetric / God Rays' },
      { value: 'neon', label: 'Neon Glow' },
      { value: 'hdri', label: 'HDRI Environment' },
      { value: 'practical', label: 'Practical (In-Scene)' },
      { value: 'rembrandt', label: 'Rembrandt' },
      { value: 'butterfly', label: 'Butterfly / Paramount' },
      { value: 'split', label: 'Split Lighting' },
      { value: 'loop', label: 'Loop Lighting' },
      { value: 'broad', label: 'Broad Lighting' },
      { value: 'short', label: 'Short Lighting' },
      { value: 'chiaroscuro', label: 'Chiaroscuro' },
      { value: 'clamshell', label: 'Clamshell Beauty' },
      { value: 'cross', label: 'Cross Lighting' },
      { value: 'kicker', label: 'Kicker / Accent' },
      { value: 'bounce', label: 'Bounce / Reflected' },
      { value: 'silhouette', label: 'Full Silhouette' },
      { value: 'high_key', label: 'High Key (Bright)' },
      { value: 'low_key', label: 'Low Key (Dark)' },
      { value: 'window', label: 'Window Light' },
      { value: 'ring_light', label: 'Ring Light' },
      { value: 'stage', label: 'Stage Spotlight' },
      { value: 'bioluminescent', label: 'Bioluminescent' },
      { value: 'underwater', label: 'Underwater Caustics' },
      { value: 'fire', label: 'Fire / Bonfire' },
      { value: 'candlelight', label: 'Candlelight' },
      { value: 'moonlight', label: 'Moonlight' },
      { value: 'dramatic', label: 'Dramatic Noir' },
    ],
  },
  {
    type: 'select', key: 'lightPreset', label: 'Cinematic Preset',
    tooltip: 'Quick lighting mood presets',
    options: [
      { value: 'none', label: 'No Preset' },
      { value: 'golden_hour', label: 'Golden Hour' },
      { value: 'blue_hour', label: 'Blue Hour' },
      { value: 'cyberpunk', label: 'Cyberpunk Neon' },
      { value: 'moody_noir', label: 'Moody Noir' },
      { value: 'natural_daylight', label: 'Natural Daylight' },
      { value: 'sunset_drama', label: 'Sunset Drama' },
      { value: 'overcast_soft', label: 'Overcast Soft' },
      { value: 'studio_beauty', label: 'Studio Beauty' },
      { value: 'horror', label: 'Horror / Eerie' },
      { value: 'romantic', label: 'Romantic Warm' },
      { value: 'sci_fi_cold', label: 'Sci-Fi Cold' },
    ],
  },
  {
    type: 'slider', key: 'intensity', label: 'Light Intensity',
    tooltip: 'Overall brightness of the light source', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'colorTempValue', label: 'Color Temperature (K)',
    tooltip: 'Color warmth: 2000K (warm amber) → 9000K (cool blue)',
    min: 2000, max: 9000, step: 100,
  },
  {
    type: 'select', key: 'colorTemp', label: 'Color Temp Preset',
    tooltip: 'Quick color temperature presets',
    options: [
      { value: 'candle', label: 'Candle (1800K)' },
      { value: 'tungsten', label: 'Tungsten (2800K)' },
      { value: 'warm', label: 'Warm (3200K)' },
      { value: 'halogen', label: 'Halogen (3500K)' },
      { value: 'fluorescent', label: 'Fluorescent (4000K)' },
      { value: 'neutral', label: 'Neutral (5000K)' },
      { value: 'daylight', label: 'Daylight (5500K)' },
      { value: 'cloudy', label: 'Cloudy (6500K)' },
      { value: 'cool', label: 'Cool (7500K)' },
      { value: 'overcast', label: 'Overcast (8000K)' },
      { value: 'shade', label: 'Shade (9000K)' },
    ],
  },
  {
    type: 'slider', key: 'shadowSoftness', label: 'Shadow Softness',
    tooltip: '0 = hard edge shadows, 100 = fully diffused soft shadows',
    advancedOnly: true, min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'lightDirection', label: 'Light Direction (°)',
    tooltip: '360° wheel: 0°=top, 90°=right, 180°=bottom, 270°=left',
    advancedOnly: true, min: 0, max: 360, step: 15,
  },
  {
    type: 'select', key: 'lightModifier', label: 'Light Modifier',
    tooltip: 'Physical modifier shaping the light',
    advancedOnly: true,
    options: [
      { value: 'none', label: 'None (Direct)' },
      { value: 'softbox', label: 'Softbox' },
      { value: 'umbrella', label: 'Umbrella' },
      { value: 'beauty_dish', label: 'Beauty Dish' },
      { value: 'snoot', label: 'Snoot' },
      { value: 'grid', label: 'Grid / Honeycomb' },
      { value: 'barn_doors', label: 'Barn Doors' },
      { value: 'diffusion', label: 'Diffusion Panel' },
      { value: 'gobo', label: 'Gobo Pattern' },
      { value: 'gel_warm', label: 'Gel — Warm CTO' },
      { value: 'gel_cool', label: 'Gel — Cool CTB' },
      { value: 'gel_color', label: 'Gel — Color' },
      { value: 'fresnel', label: 'Fresnel Lens' },
      { value: 'reflector', label: 'Reflector' },
      { value: 'scrim', label: 'Scrim / Flag' },
    ],
  },
  {
    type: 'toggle', key: 'godRays', label: 'God Rays',
    tooltip: 'Volumetric light beams through atmosphere', advancedOnly: true,
  },
  {
    type: 'slider', key: 'fogDensity', label: 'Fog / Haze Density',
    tooltip: 'Amount of atmospheric fog interacting with light',
    advancedOnly: true, min: 0, max: 100, step: 5,
  },
];

// ══════════════════════════════════════
// CHARACTER PANEL FIELDS
// ══════════════════════════════════════

export const CHARACTER_FIELDS: FieldConfig[] = [
  {
    type: 'text', key: 'subject', label: 'Subject Description',
    tooltip: 'Describe who or what the character is',
  },
  {
    type: 'select', key: 'pose', label: 'Pose',
    tooltip: 'Body position and stance',
    options: [
      { value: 'standing', label: 'Standing' },
      { value: 'sitting', label: 'Sitting' },
      { value: 'walking', label: 'Walking' },
      { value: 'running', label: 'Running' },
      { value: 'crouching', label: 'Crouching' },
      { value: 'lying', label: 'Lying Down' },
      { value: 'leaning', label: 'Leaning' },
      { value: 'jumping', label: 'Jumping / Mid-Air' },
      { value: 'kneeling', label: 'Kneeling' },
      { value: 'hero_pose', label: 'Hero Pose' },
      { value: 'action', label: 'Action / Dynamic' },
      { value: 'casual', label: 'Casual Relaxed' },
      { value: 'dancing', label: 'Dancing' },
      { value: 'fighting', label: 'Fighting Stance' },
      { value: 'meditating', label: 'Meditating' },
      { value: 'profile_pose', label: 'Profile Side' },
      { value: 'back_turned', label: 'Back Turned' },
      { value: 'power_pose', label: 'Power Pose' },
      { value: 'contemplative', label: 'Contemplative' },
    ],
  },
  {
    type: 'select', key: 'expression', label: 'Facial Expression',
    tooltip: 'Primary emotion shown on face',
    options: [
      { value: 'neutral', label: 'Neutral' },
      { value: 'happy', label: 'Happy / Smiling' },
      { value: 'sad', label: 'Sad / Melancholic' },
      { value: 'angry', label: 'Angry / Fierce' },
      { value: 'surprised', label: 'Surprised' },
      { value: 'thoughtful', label: 'Thoughtful / Pensive' },
      { value: 'mysterious', label: 'Mysterious' },
      { value: 'confident', label: 'Confident' },
      { value: 'fearful', label: 'Fearful' },
      { value: 'serene', label: 'Serene / Peaceful' },
      { value: 'intense', label: 'Intense / Focused' },
      { value: 'playful', label: 'Playful' },
      { value: 'emotional', label: 'Emotional / Tearful' },
      { value: 'joyful', label: 'Joyful / Ecstatic' },
      { value: 'stoic', label: 'Stoic / Unreadable' },
    ],
  },
  {
    type: 'select', key: 'microExpression', label: 'Micro-Expression',
    tooltip: 'Subtle secondary facial detail',
    advancedOnly: true,
    options: [
      { value: 'none', label: 'None' },
      { value: 'slight_smirk', label: 'Slight Smirk' },
      { value: 'furrowed_brow', label: 'Furrowed Brow' },
      { value: 'raised_eyebrow', label: 'Raised Eyebrow' },
      { value: 'clenched_jaw', label: 'Clenched Jaw' },
      { value: 'nostril_flare', label: 'Nostril Flare' },
      { value: 'lip_bite', label: 'Lip Bite' },
      { value: 'squinting', label: 'Squinting' },
      { value: 'widened_eyes', label: 'Widened Eyes' },
      { value: 'trembling_lip', label: 'Trembling Lip' },
      { value: 'dimpled_smile', label: 'Dimpled Smile' },
    ],
  },
  {
    type: 'select', key: 'eyeDirection', label: 'Eye Direction',
    tooltip: 'Where the character is looking',
    advancedOnly: true,
    options: [
      { value: 'camera', label: 'Direct to Camera' },
      { value: 'left', label: 'Looking Left' },
      { value: 'right', label: 'Looking Right' },
      { value: 'up', label: 'Looking Up' },
      { value: 'down', label: 'Looking Down' },
      { value: 'distant', label: 'Distant / Far Away' },
      { value: 'closed', label: 'Eyes Closed' },
      { value: 'over_shoulder', label: 'Over Shoulder Glance' },
      { value: 'sidelong', label: 'Sidelong Glance' },
    ],
  },
  {
    type: 'select', key: 'characterFraming', label: 'Body Framing',
    tooltip: 'How much of the body is visible',
    options: [
      { value: 'full', label: 'Full Body' },
      { value: 'three_quarter', label: 'Three-Quarter' },
      { value: 'waist_up', label: 'Waist Up' },
      { value: 'bust', label: 'Bust / Chest Up' },
      { value: 'head_shoulders', label: 'Head & Shoulders' },
      { value: 'face_only', label: 'Face Only' },
      { value: 'detail', label: 'Detail / Hands / Eyes' },
    ],
  },
  {
    type: 'slider', key: 'bodyRotation', label: 'Body Rotation (°)',
    tooltip: 'Rotation of the body on vertical axis',
    advancedOnly: true, min: 0, max: 360, step: 15,
  },
  {
    type: 'slider', key: 'gestureIntensity', label: 'Gesture Intensity',
    tooltip: 'How pronounced the body language / gestures are',
    advancedOnly: true, min: 0, max: 100, step: 10,
  },
];

// ══════════════════════════════════════
// QUALITY & RENDERING FIELDS (Global)
// ══════════════════════════════════════

export const QUALITY_FIELDS: FieldConfig[] = [
  {
    type: 'select', key: 'renderStyle', label: 'Rendering Style',
    tooltip: 'Overall visual rendering approach',
    options: [
      { value: 'photorealistic', label: 'Photorealistic' },
      { value: 'hyperreal', label: 'Hyperreal / Ultra Detail' },
      { value: 'stylized', label: 'Stylized / Artistic' },
      { value: 'game_engine', label: 'Game Engine (Unreal/Unity)' },
      { value: 'ray_traced', label: 'Ray Traced' },
      { value: 'path_traced', label: 'Path Traced' },
      { value: 'cel_shaded', label: 'Cel Shaded / Toon' },
      { value: 'painterly', label: 'Painterly' },
      { value: 'mixed_media', label: 'Mixed Media' },
    ],
  },
  {
    type: 'slider', key: 'textureDetail', label: 'Texture Detail',
    tooltip: 'Level of surface texture detail', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'skinDetail', label: 'Skin Detail Enhancer',
    tooltip: 'Pore-level skin rendering detail',
    advancedOnly: true, min: 0, max: 100, step: 5,
  },
  {
    type: 'toggle', key: 'filmGrain', label: 'Film Grain',
    tooltip: 'Add organic film grain texture',
  },
  {
    type: 'slider', key: 'hdrControl', label: 'HDR Intensity',
    tooltip: 'High Dynamic Range brightness control',
    advancedOnly: true, min: 0, max: 100, step: 5,
  },
  {
    type: 'select', key: 'resolutionScale', label: 'Resolution',
    tooltip: 'Output resolution target',
    options: [
      { value: 'sd', label: 'SD (480p)' },
      { value: 'hd', label: 'HD (720p)' },
      { value: 'full_hd', label: 'Full HD (1080p)' },
      { value: '2k', label: '2K (1440p)' },
      { value: '4k', label: '4K UHD' },
      { value: '8k', label: '8K Ultra' },
    ],
  },
  {
    type: 'select', key: 'dynamicRange', label: 'Dynamic Range',
    tooltip: 'Contrast range between darks and brights',
    advancedOnly: true,
    options: [
      { value: 'low', label: 'Low (Flat)' },
      { value: 'standard', label: 'Standard' },
      { value: 'high', label: 'High' },
      { value: 'extreme', label: 'Extreme (HDR10+)' },
    ],
  },
  {
    type: 'toggle', key: 'realCameraMode', label: 'Real Camera Simulation',
    tooltip: 'Simulate real camera imperfections (lens aberrations, vignetting)',
    advancedOnly: true,
  },
];

// ══════════════════════════════════════
// PERSPECTIVE & COMPOSITION FIELDS (Global)
// ══════════════════════════════════════

export const PERSPECTIVE_FIELDS: FieldConfig[] = [
  {
    type: 'toggle', key: 'ruleOfThirds', label: 'Rule of Thirds',
    tooltip: 'Apply rule of thirds composition guideline',
  },
  {
    type: 'toggle', key: 'goldenRatio', label: 'Golden Ratio Overlay',
    tooltip: 'Apply golden ratio spiral composition overlay',
  },
  {
    type: 'toggle', key: 'symmetryMode', label: 'Symmetry Mode',
    tooltip: 'Force symmetrical composition',
  },
  {
    type: 'toggle', key: 'centeredCinematic', label: 'Centered Cinematic',
    tooltip: 'Center-framed cinematic composition style',
  },
  {
    type: 'select', key: 'framingPreset', label: 'Framing Preset',
    tooltip: 'Quick framing template',
    options: [
      { value: 'none', label: 'Custom' },
      { value: 'portrait', label: 'Portrait' },
      { value: 'product', label: 'Product' },
      { value: 'wide_cinematic', label: 'Wide Cinematic' },
      { value: 'editorial', label: 'Editorial' },
      { value: 'fashion', label: 'Fashion Campaign' },
      { value: 'billboard', label: 'Billboard' },
    ],
  },
  {
    type: 'slider', key: 'subjectCentering', label: 'Subject Centering',
    tooltip: '0 = far off-center, 50 = centered, 100 = far off-center right',
    min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'negativeSpace', label: 'Negative Space',
    tooltip: 'Amount of empty space around the subject',
    min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'horizonTilt', label: 'Camera Tilt (°)',
    tooltip: 'Tilt the horizon line for dynamic effect',
    advancedOnly: true, min: -45, max: 45, step: 5,
  },
  {
    type: 'slider', key: 'cameraHeight', label: 'Camera Height',
    tooltip: '0 = ground level, 100 = overhead',
    advancedOnly: true, min: 0, max: 100, step: 5,
  },
];

// ══════════════════════════════════════
// VFX & ATMOSPHERE FIELDS (Global)
// ══════════════════════════════════════

export const VFX_FIELDS: FieldConfig[] = [
  {
    type: 'slider', key: 'fogHaze', label: 'Fog / Haze',
    tooltip: 'Atmospheric fog and haze intensity', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'cinematicSmoke', label: 'Smoke Layers',
    tooltip: 'Layered atmospheric smoke effect', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'dustParticles', label: 'Dust Particles',
    tooltip: 'Floating dust particle density', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'godRaysGlobal', label: 'God Rays / Light Rays',
    tooltip: 'Volumetric light beam intensity', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'bloomGlow', label: 'Bloom / Glow',
    tooltip: 'Light bloom and glow effect', min: 0, max: 100, step: 5,
  },
  {
    type: 'toggle', key: 'filmGrain', label: 'Film Grain',
    tooltip: 'Add organic film grain texture',
  },
  {
    type: 'slider', key: 'chromaticAberration', label: 'Chromatic Aberration',
    tooltip: 'Color fringing at edges for cinematic look',
    min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'motionBlurGlobal', label: 'Motion Blur',
    tooltip: 'Global motion blur intensity', min: 0, max: 100, step: 5,
  },
  {
    type: 'select', key: 'particles', label: 'Particle System',
    tooltip: 'Atmospheric particle effects',
    options: [
      { value: 'none', label: 'None' },
      { value: 'dust', label: 'Dust Motes' },
      { value: 'sparks', label: 'Sparks' },
      { value: 'embers', label: 'Embers' },
      { value: 'snow', label: 'Snow' },
      { value: 'rain', label: 'Rain' },
      { value: 'ash', label: 'Ash / Debris' },
      { value: 'petals', label: 'Flower Petals' },
      { value: 'fireflies', label: 'Fireflies' },
      { value: 'confetti', label: 'Confetti' },
      { value: 'bubbles', label: 'Bubbles' },
      { value: 'leaves', label: 'Falling Leaves' },
      { value: 'smoke_wisps', label: 'Smoke Wisps' },
      { value: 'magic_particles', label: 'Magic Particles' },
    ],
  },
  {
    type: 'slider', key: 'atmosphericDensity', label: 'Atmospheric Density',
    tooltip: 'Overall atmospheric haze and density', min: 0, max: 100, step: 5,
  },
  {
    type: 'toggle', key: 'motionTrails', label: 'Motion Trails',
    tooltip: 'Light/motion trail effects behind moving elements',
    advancedOnly: true,
  },
  {
    type: 'toggle', key: 'energyAura', label: 'Energy Aura',
    tooltip: 'Glowing energy aura around subjects',
    advancedOnly: true,
  },
  {
    type: 'toggle', key: 'lightStreaks', label: 'Light Streaks',
    tooltip: 'Anamorphic light streak effects',
    advancedOnly: true,
  },
  {
    type: 'toggle', key: 'glitchEffect', label: 'Glitch Effect',
    tooltip: 'Digital glitch / databend visual distortion',
    advancedOnly: true,
  },
  {
    type: 'slider', key: 'explosionIntensity', label: 'Explosion Intensity',
    tooltip: 'Pyrotechnic explosion effect level',
    advancedOnly: true, min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'fireFlame', label: 'Fire & Flame',
    tooltip: 'Fire and flame overlay intensity',
    advancedOnly: true, min: 0, max: 100, step: 5,
  },
  {
    type: 'toggle', key: 'lightningOverlay', label: 'Lightning Overlay',
    tooltip: 'Dramatic lightning bolt overlay',
    advancedOnly: true,
  },
];

// ══════════════════════════════════════
// COLOR GRADING FIELDS (Global)
// ══════════════════════════════════════

export const COLOR_GRADING_FIELDS: FieldConfig[] = [
  {
    type: 'select', key: 'lutPreset', label: 'LUT Preset',
    tooltip: 'Color Look-Up Table for cinematic grading',
    options: [
      { value: 'none', label: 'None' },
      { value: 'teal_orange', label: 'Teal & Orange' },
      { value: 'blockbuster', label: 'Blockbuster' },
      { value: 'bleach_bypass', label: 'Bleach Bypass' },
      { value: 'day_for_night', label: 'Day for Night' },
      { value: 'cross_process', label: 'Cross Process' },
      { value: 'matrix_green', label: 'Matrix Green' },
      { value: 'golden_warm', label: 'Golden Warm' },
      { value: 'arctic_cool', label: 'Arctic Cool' },
      { value: 'sepia', label: 'Sepia Tone' },
      { value: 'noir', label: 'Film Noir B&W' },
      { value: 'pastel_dream', label: 'Pastel Dream' },
      { value: 'high_fashion', label: 'High Fashion' },
    ],
  },
  {
    type: 'select', key: 'filmStock', label: 'Film Stock Simulation',
    tooltip: 'Emulate classic film stock characteristics',
    options: [
      { value: 'none', label: 'Digital' },
      { value: 'kodak_portra_400', label: 'Kodak Portra 400' },
      { value: 'kodak_ektar_100', label: 'Kodak Ektar 100' },
      { value: 'fuji_pro_400h', label: 'Fuji Pro 400H' },
      { value: 'fuji_velvia_50', label: 'Fuji Velvia 50' },
      { value: 'ilford_hp5', label: 'Ilford HP5+ B&W' },
      { value: 'kodak_tri_x', label: 'Kodak Tri-X 400 B&W' },
      { value: 'cinestill_800t', label: 'CineStill 800T' },
      { value: 'kodachrome_64', label: 'Kodachrome 64' },
      { value: 'polaroid_600', label: 'Polaroid 600' },
    ],
  },
  {
    type: 'slider', key: 'contrastCurve', label: 'Contrast',
    tooltip: 'S-curve contrast control', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'highlightBalance', label: 'Highlights',
    tooltip: 'Highlight recovery and brightness', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'shadowBalance', label: 'Shadows',
    tooltip: 'Shadow lift and depth', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'saturationIntensity', label: 'Saturation',
    tooltip: 'Color saturation intensity', min: 0, max: 100, step: 5,
  },
  {
    type: 'select', key: 'splitToningHighlight', label: 'Split Tone — Highlights',
    tooltip: 'Color cast for bright areas',
    advancedOnly: true,
    options: [
      { value: 'none', label: 'None' },
      { value: 'warm_gold', label: 'Warm Gold' },
      { value: 'cool_blue', label: 'Cool Blue' },
      { value: 'rose', label: 'Rose Pink' },
      { value: 'amber', label: 'Amber' },
      { value: 'cyan', label: 'Cyan' },
      { value: 'lavender', label: 'Lavender' },
    ],
  },
  {
    type: 'select', key: 'splitToningShadow', label: 'Split Tone — Shadows',
    tooltip: 'Color cast for dark areas',
    advancedOnly: true,
    options: [
      { value: 'none', label: 'None' },
      { value: 'deep_blue', label: 'Deep Blue' },
      { value: 'teal', label: 'Teal' },
      { value: 'purple', label: 'Purple' },
      { value: 'brown', label: 'Warm Brown' },
      { value: 'green', label: 'Forest Green' },
      { value: 'slate', label: 'Slate Gray' },
    ],
  },
  {
    type: 'toggle', key: 'tealOrangePreset', label: 'Cinematic Teal & Orange',
    tooltip: 'Apply classic Hollywood teal & orange grade',
  },
  {
    type: 'toggle', key: 'vintageMode', label: 'Vintage / Retro',
    tooltip: 'Apply vintage film look with faded blacks and warm tones',
  },
];

// ══════════════════════════════════════
// TEXTURE & DETAIL FIELDS (Global)
// ══════════════════════════════════════

export const TEXTURE_FIELDS: FieldConfig[] = [
  {
    type: 'slider', key: 'microDetail', label: 'Micro-Detail Enhancement',
    tooltip: 'Ultra-fine surface detail rendering', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'sharpness', label: 'Sharpness',
    tooltip: 'Image sharpening intensity', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'surfaceClarity', label: 'Surface Clarity',
    tooltip: 'Midtone contrast for texture pop', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'fabricTexture', label: 'Fabric Texture Boost',
    tooltip: 'Enhance cloth, fabric, and material detail', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'highFrequency', label: 'High Frequency Detail',
    tooltip: 'Fine edge and hair-level detail', min: 0, max: 100, step: 5,
  },
];

// ══════════════════════════════════════
// SKIN & BEAUTY FIELDS (Global)
// ══════════════════════════════════════

export const SKIN_BEAUTY_FIELDS: FieldConfig[] = [
  {
    type: 'slider', key: 'skinSmoothing', label: 'Skin Smoothing',
    tooltip: '0 = natural, 100 = editorial beauty retouching', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'texturePreservation', label: 'Texture Preservation',
    tooltip: 'Retain skin texture while smoothing', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'poreRefinement', label: 'Pore Refinement',
    tooltip: 'Soften pore visibility', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'blemishRemoval', label: 'Blemish Removal',
    tooltip: 'Remove skin imperfections', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'underEyeCorrection', label: 'Under-Eye Correction',
    tooltip: 'Reduce dark circles and eye bags', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'skinToneBalance', label: 'Skin Tone Balance',
    tooltip: 'Even out skin tone across face and body', min: 0, max: 100, step: 5,
  },
  {
    type: 'slider', key: 'highlightGlow', label: 'Highlight Glow',
    tooltip: 'Luminous skin highlight effect', min: 0, max: 100, step: 5,
  },
  {
    type: 'toggle', key: 'freqSeparation', label: 'Frequency Separation',
    tooltip: 'Simulate frequency separation retouching technique',
    advancedOnly: true,
  },
  {
    type: 'select', key: 'beautyPreset', label: 'Beauty Preset',
    tooltip: 'Quick beauty retouching preset',
    options: [
      { value: 'none', label: 'None' },
      { value: 'natural_soft', label: 'Natural Soft' },
      { value: 'editorial', label: 'Editorial Beauty' },
      { value: 'commercial', label: 'Commercial Campaign' },
      { value: 'fashion_high', label: 'High Fashion' },
      { value: 'glamour', label: 'Glamour' },
      { value: 'skin_care', label: 'Skincare Ad' },
    ],
  },
  {
    type: 'select', key: 'naturalFashionToggle', label: 'Beauty Mode',
    tooltip: 'Natural look vs fashion campaign look',
    options: [
      { value: 'natural', label: 'Natural' },
      { value: 'fashion', label: 'Fashion Campaign' },
    ],
  },
];

// ══════════════════════════════════════
// RENDERING & OUTPUT FIELDS (Global)
// ══════════════════════════════════════

export const OUTPUT_FIELDS: FieldConfig[] = [
  {
    type: 'select', key: 'renderQuality', label: 'Render Quality',
    tooltip: 'Output render quality level',
    options: [
      { value: 'draft', label: 'Draft (Fast)' },
      { value: 'standard', label: 'Standard' },
      { value: 'high', label: 'High Quality' },
      { value: '4k', label: '4K Ultra HD' },
      { value: '8k', label: '8K Ultra' },
    ],
  },
  {
    type: 'slider', key: 'noiseReduction', label: 'Noise Reduction',
    tooltip: 'Reduce digital noise and artifacts', min: 0, max: 100, step: 5,
  },
  {
    type: 'select', key: 'upscaleMode', label: 'Upscale Mode',
    tooltip: 'AI-based upscaling algorithm',
    options: [
      { value: 'none', label: 'None' },
      { value: 'bilinear', label: 'Bilinear' },
      { value: 'lanczos', label: 'Lanczos' },
      { value: 'ai_esrgan', label: 'AI ESRGAN' },
      { value: 'ai_topaz', label: 'AI Topaz-style' },
    ],
  },
  {
    type: 'select', key: 'exportProfile', label: 'Export Profile',
    tooltip: 'Optimize output for target platform',
    options: [
      { value: 'web', label: 'Web / Digital' },
      { value: 'print', label: 'Print (300 DPI)' },
      { value: 'social', label: 'Social Media' },
      { value: 'billboard', label: 'Billboard / Large Format' },
      { value: 'video', label: 'Video Frame' },
    ],
  },
];
